import moment from 'moment'
import apollo from '@/graphql'

import cache from '@/store/helpers/cache.helper'

import CampaignDetailsQuery from '@/graphql/campaign/CampaignDetailsQuery.gql'
import CampaignDetailsForAccountsTeamQuery from '@/graphql/campaign2/CampaignDetailsForAccountsTeamQuery.gql'
import CampaignsQuery from '@/graphql/campaign/CampaignsQuery.gql'
import CampaignTypesQuery from '@/graphql/campaign/CampaignTypesQuery.gql'
import CampaignCategoriesQuery from '@/graphql/campaign/CampaignCategoriesQuery.gql'
import CloneCampaign from '@/graphql/campaign/CloneCampaign.gql'
import DeleteCampaign from '@/graphql/campaign/DeleteCampaign.gql'
import GetCampaignTargetingCsvData from '@/graphql/campaign2/GetCampaignTargetingCsvData.gql'
import LoadAdAccountsForCampaignCreation from '@/graphql/campaign2/LoadAdAccountsForCampaignCreation.gql'
import SaveCampaignDetails from '@/graphql/campaign/SaveCampaignDetails.gql'
import SaveCampaignMgmt from '@/graphql/campaign2/SaveCampaignMgmt.gql'
import ValidateCampaignTargetingQuery from '@/graphql/campaign2/ValidateCampaignTargetingQuery.gql'

import UploadCampaignVideo from '@/graphql/campaign/UploadCampaignVideo.gql'

// initial state
const state = {
  activeStores: [],
  campaignDetails: {},
  campaignTypes: [],
  campaignCategories: [],
  draftStores: [],
  editableCampaignFields: {},
  isSavingCampaign: false,
}

// getters
const getters = {
  activeStores: st => st.activeStores,
  campaignDetails: st => st.campaignDetails,
  campaignTypes: st => st.campaignTypes,
  campaignCategories: st => st.campaignCategories,
  draftStores: st => st.draftStores,
  editableCampaignFields: st => st.editableCampaignFields,
  isSavingCampaign: st => st.isSavingCampaign,
}

// function copyRetailerFields(retailer) {
//   return { id: retailer.id }
// }

// function copyStoreFields(store) {
//   return { id: store.id }
// }

export function getAcceptableDurations() {
  /**
   * Function is exported to be used
   * in src/store/modules/video/VideoDetails.js
   */

  const durations = []

  const minDuration = 6
  const maxDuration = 30

  for (let i = minDuration; i <= maxDuration; i += 1) {
    durations.push(i)
  }

  return durations
}


// mutations
const mutations = {
  createCampaignDetails() {
    state.activeStores = []
    state.editableCampaignFields = {
      activeStores: [],
      companiesId: null,
      creativeType: 'video',
      emailRecipients: [], // set through UI when creating campaign
      emailUsers: [], // set on the backend, contains user's objects
      selectedStores: [],
      selectedRetailers: [],
      selectedBanners: [],
      duration: 6, // Preselect 6 seconds in the UI
      flightDates: [],
      isGlobal: false,
      tmpVideo: null,
      videosId: null,
      campaignTypesId: 3, // hardcoded because select dropdown only displayed for super-admin
      hubspotDealId: '',
      creativeAspectRatio: '16:9',
    }

    state.campaignDetails = {
      name: 'New Campaign',
    }
  },

  setActiveStores(st, campaignDetails) {
    // don't need a separate assignStore function (yet)
    state.activeStores = campaignDetails.activeStores
  },

  setDraftStores(st, campaignDetails) {
    // don't need a separate assignStore function (yet)
    state.draftStores = campaignDetails.draftStores
  },

  setEditableCampaignFields(st, campaignDetails) {
    state.editableCampaignFields = {
      // Formatting timestamps here because field is used
      // in v-model on date-picker component
      duration: campaignDetails.video.duration,
      id: campaignDetails.id,
      isExpired: campaignDetails.isExpired,
      name: campaignDetails.name,
      creativeType: null,
      companiesId: campaignDetails.companiesId,
      campaignTypesId: campaignDetails.campaignTypesId,
      isGlobal: campaignDetails.isGlobal,
      selectedStores: [],
      selectedRetailers: [],
      selectedBanners: [],
      selector: 1,
      status: campaignDetails.status,
      startDate: moment.utc(+campaignDetails.startDate).format('YYYY-MM-DD'),
      endDate: moment.utc(+campaignDetails.endDate).format('YYYY-MM-DD'),
      tmpVideo: campaignDetails.tmpVideo,
      videosId: campaignDetails.videosId,
      hubspotDealId: campaignDetails.hubspotDealId,
      creativeAspectRatio: campaignDetails.creativeAspectRatio,
      storeAreasId: campaignDetails.storeAreasId,
    }
  },

  setTmpVideoField(st, { tmpVideo, duration }) {
    state.editableCampaignFields.tmpVideo = tmpVideo
    state.editableCampaignFields.duration = duration
  },

  setCampaignDetails(st, campaignDetails) {
    state.campaignDetails = campaignDetails
  },

  setCampaignTypes(st, campaignTypes) {
    state.campaignTypes = campaignTypes
  },

  setCampaignCategories(st, campaignCategories) {
    state.campaignCategories = campaignCategories
  },

  setIsSavingCampaign(st, isSavingCampaign) {
    state.isSavingCampaign = isSavingCampaign
  },
}

// actions
const actions = {
  async loadCampaignDetails({ state, commit }, { id, isInitialLoad }) {
    if (isInitialLoad) {
      state.campaignDetails = {}
    }

    const { data: { campaignDetails } } = await apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: CampaignDetailsQuery,
      variables: { id: +id },
    })

    commit('setActiveStores', campaignDetails)
    commit('setCampaignDetails', campaignDetails)
    commit('setDraftStores', campaignDetails)
    commit('setEditableCampaignFields', campaignDetails)
    commit('setIsSavingCampaign', false)
  },
  loadCampaignDetailsForAccountsTeam(context, { id }) {
    return apollo.defaultClient
      .query({
        query: CampaignDetailsForAccountsTeamQuery,
        fetchPolicy: 'network-only',
        variables: {
          id,
        },
      })
  },
  loadCampaignTypes({ commit }) {
    apollo.defaultClient
      .watchQuery({
        query: CampaignTypesQuery,
      })
      .subscribe(({ data: { campaignTypes } }) => {
        commit('setCampaignTypes', campaignTypes)
      })
  },
  loadCampaignCategories({ commit }) {
    apollo.defaultClient
      .watchQuery({
        query: CampaignCategoriesQuery,
      })
      .subscribe(({ data: { campaignCategories } }) => {
        commit('setCampaignCategories', campaignCategories)
      })
  },
  cloneCampaign(context, { id, $router }) {
    apollo.defaultClient
      .mutate({
        mutation: CloneCampaign,
        variables: { id },
      })
      .then(({ data: { cloneCampaign: newId } }) => {
        $router.push(`/campaign/${newId}`)
      })
  },
  deleteCampaign(context, { id, $message, $router }) {
    apollo.defaultClient
      .mutate({
        mutation: DeleteCampaign,
        variables: { id },
      })
      .then(() => {
        $message.success('Campaign deleted successfully')
        cache.set('campaignList', undefined)
        $router.push('/campaigns')
      })
      .catch((err) => {
        $message.error(err.graphQLErrors[0].message)
      })
  },

  getCampaignTargetingCsvData(context, variables) {
    return apollo.defaultClient.query({
      query: GetCampaignTargetingCsvData,
      fetchPolicy: 'network-only',
      variables,
    })
  },

  loadAdAccounts(context, variables) {
    return apollo.defaultClient.query({
      query: LoadAdAccountsForCampaignCreation,
      variables,
    })
  },

  saveCampaignDetails({
    commit,
    getters,
    dispatch,
  }, {
    $message,
    $router,
    selection,
  }) {
    commit('setIsSavingCampaign', true)

    apollo.defaultClient
      .mutate({
        mutation: SaveCampaignDetails,
        variables: {
          fields: {
            campaignTypesId: state.editableCampaignFields.campaignTypesId,
            companiesId: state.editableCampaignFields.companiesId || getters['Auth/user'].companiesId,
            duration: state.editableCampaignFields.duration,
            emailRecipients: state.editableCampaignFields.emailRecipients,
            endDate: state.editableCampaignFields.endDate,
            id: state.editableCampaignFields.id,
            isGlobal: state.editableCampaignFields.isGlobal,
            name: state.editableCampaignFields.name,
            startDate: state.editableCampaignFields.startDate,
            tmpVideo: state.editableCampaignFields.tmpVideo,
            hubspotDealId: state.editableCampaignFields.hubspotDealId,
            creativeAspectRatio: state.editableCampaignFields.creativeAspectRatio,
            storeAreasId: state.editableCampaignFields.storeAreasId,
            propertyValueIds: state.editableCampaignFields.propertyValueIds,
            selection,
          },
        },
        refetchQueries: [state.campaignDetails.id]
          .filter(id => id)
          .map(id => ({
            query: CampaignDetailsQuery,
            variables: { id },
          }))
          .concat([
            {
              query: CampaignsQuery,
              variables: { companiesId: this.getters['Auth/user'].companiesId },
            },
          ]),
      })
      .then(({ data: { saveCampaignDetails: { id } } }) => {
        $message({
          message: 'Changes saved',
          type: 'success',
        })

        dispatch('loadCampaignDetails', {
          id,
          isInitialLoad: false,
        })

        if (!state.editableCampaignFields.id) {
          $router.push(`/campaign/${id}`)
        }
      })
      .catch((err) => {
        $message({
          message: err.message,
          type: 'error',
        })
        commit('setIsSavingCampaign', false)
      })
  },

  saveCampaignMgmt(context, variables) {
    return apollo.defaultClient
      .mutate({
        mutation: SaveCampaignMgmt,
        variables,
      })
  },

  async uploadCampaignVideo(context, { file }) {
    if (!state.editableCampaignFields.storeAreasId) {
      throw new Error()
    }

    try {
      const {
        data: {
          uploadCampaignVideo: { id, duration },
        },
      } = await apollo.defaultClient.mutate({
        mutation: UploadCampaignVideo,
        variables: {
          fields: {
            companiesId: state.editableCampaignFields.campaignTypesId === 1
              ? state.editableCampaignFields.companiesId
              : +(this.getters['Auth/user'].companiesId),
            duration: state.editableCampaignFields.duration,
            name: file.name,
            isTemplate: false,
            aspectRatio: state.editableCampaignFields.creativeAspectRatio,
            storeAreasId: state.editableCampaignFields.storeAreasId,
          },
          file,
        },
        refetchQueries: [state.campaignDetails.id]
          .filter(campaignId => campaignId)
          .map(campaignId => ({
            query: CampaignDetailsQuery,
            variables: { id: +campaignId },
          })),
      })
      context.commit('setTmpVideoField', { tmpVideo: id, duration: Math.round(duration / 1000) })
    } catch (err) {
      throw new Error(err.message)
    }
  },

  validateCampaignTargeting(context, variables) {
    return apollo.defaultClient.query({
      query: ValidateCampaignTargetingQuery,
      fetchPolicy: 'network-only',
      variables,
    })
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/campaign/CampaignDetails.js